
import React, { useEffect, useRef } from 'react';
import { FileText, Download, Palette } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}

const FeatureCard = ({ icon, title, description, delay = 0 }: FeatureCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('reveal-visible');
            }, delay);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [delay]);

  return (
    <Card 
      ref={cardRef} 
      className="reveal border border-ocean-200 shadow-md hover:shadow-blue transition-all duration-300 hover:-translate-y-1 overflow-hidden bg-white/80 backdrop-blur-sm"
    >
      <CardHeader className="flex items-center pb-2">
        <div className="h-14 w-14 rounded-full flex items-center justify-center bg-ocean-100 text-ocean-600 mb-4">
          {icon}
        </div>
        <CardTitle className="text-2xl text-ocean-700">{title}</CardTitle>
      </CardHeader>
      <CardContent className="text-center">
        <p className="text-ocean-600">{description}</p>
      </CardContent>
    </Card>
  );
};

const FeaturesSection = () => {
  return (
    <section id="features" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-ocean-800 mb-4">
            Everything You Need for Professional Dive Quotes
          </h2>
          <p className="text-xl text-ocean-600 max-w-2xl mx-auto">
            Built specifically for dive shop operations, with features that simplify your quoting process
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<FileText size={32} />}
            title="Customizable Quote Builder"
            description="Easily add dive courses, equipment rentals, and packages with custom pricing and descriptions."
            delay={0}
          />
          <FeatureCard
            icon={<Download size={32} />}
            title="PDF Export & Local Saving"
            description="Generate professional PDF quotes and save templates locally - no internet connection needed."
            delay={200}
          />
          <FeatureCard
            icon={<Palette size={32} />}
            title="Branded Templates & Layouts"
            description="Customize colors, logos and layouts to match your dive shop's unique brand identity."
            delay={400}
          />
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
